/**
 * ActionExecutor — TF-209 / PF-014
 *
 * Executes step-level actions returned by the evaluate-step API.
 * Actions are executed in order after the experience renders.
 *
 * MVP Actions:
 *   - go_to_url — Navigate to a specified URL
 *   - show_leads_popup — Show a leads capture popup (email collection)
 *   - display_offer — Display an offer popup from a required template
 *
 * PF-014: Popup actions require a `template_id`. The template's page_content
 * is fetched from the API, rendered via BlockRenderer, and presented inside
 * the shared overlay/modal shell. Missing or invalid templates fail closed.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useFunnelAnalytics } from '../analytics';
import { useFunnelContext } from '../funnel-provider';
import { evaluateStepHook, isFunnelUiEventsEnabled } from '../events';
import type {
  FunnelAddToCartCallback,
  FunnelOffer,
  StepPageBlock,
  TemplateContentResponse,
  RuntimeStepAction,
  ActionConfig,
} from '../types';
import { normalizeBlocks, renderBlocks } from './BlockRenderer';
import type { BlockCallbacks } from './ComponentRenderers';
import { funnelLogger } from '../logger';
import { getActionHandler } from '../handlers/registry';
import { dispatchAction } from '../handlers/dispatcher';
import type { TssEventEnvelope, UIEventPayload } from '../events/envelope';
import type { SemanticResultOptions } from '../handlers/types';

// OAF-027/030: StepAction and ActionConfig now imported from types.ts — single source of truth.

interface ActionExecutorProps {
  /**
   * H5 loop guard: hop depth of the envelope that triggered these actions
   * (reaction executors). dispatch_event re-emits at hopCount+1 so cycles
   * terminate at MAX_EVENT_HOPS. Defaults to 0 for step-authored actions.
   */
  hopCount?: number;
  actions: RuntimeStepAction[];
  /** Lifecycle mode from the funnel — controls how often actions fire. */
  triggerLifecycle?: string;
  /** Unique step ID to track execution per-step. */
  stepId: string;
  /** Cart mutations are authorized and owned by the host-level funnel flow. */
  onAddToCart?: FunnelAddToCartCallback;
  /** Callback when a displayed offer is accepted. */
  onOfferAccept?: (
    offerId: string,
    options?: SemanticResultOptions
  ) => void | boolean | Promise<void | boolean>;
  /** Callback when a displayed offer is declined. */
  onOfferDecline?: (
    offerId: string,
    options?: SemanticResultOptions
  ) => void | boolean | Promise<void | boolean>;
  /** Active offer fallback for display_offer popups when inline copy is omitted. */
  fallbackOffer?: Partial<FunnelOffer> | null;
  /** False while the signed cart-pricing proof dependency chain is inactive. */
  commerceEnabled?: boolean;
  /** Intent that activated a managed semantic listener. */
  intentEnvelope?: TssEventEnvelope<UIEventPayload>;
  /** Lifecycle hook invoked only after the action reports success. */
  onActionSucceeded?: (action: RuntimeStepAction) => void;
  /** Completion hook used by reaction listeners to release their in-flight lock. */
  onActionSettled?: (action: RuntimeStepAction, succeeded: boolean) => void;
}

const DEPRECATED_FREE_GIFT_POPUP_ACTION = 'show_free_gift_popup';
const DEPRECATED_FREE_GIFT_POPUP_MESSAGE =
  'Free-gift popups must be authored as campaigns (type=freegift_popup). Funnel action `show_free_gift_popup` is no longer supported.';

function resolveActionTemplateId(config: ActionConfig): string | undefined {
  if (typeof config.template_id === 'string' && config.template_id.trim()) {
    return config.template_id;
  }
  const camel = (config as Record<string, unknown>).templateId;
  if (typeof camel === 'string' && camel.trim()) {
    return camel;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Lifecycle helpers
// ---------------------------------------------------------------------------

function getStorageKey(stepId: string, actionId: string): string {
  return `funnel_action_${stepId}_${actionId}`;
}

function isDevelopmentRuntime(): boolean {
  return Boolean(import.meta.env?.DEV || process.env.NODE_ENV === 'development');
}

function hasActionFired(stepId: string, actionId: string, lifecycle: string): boolean {
  if (isDevelopmentRuntime()) {
    return false;
  }
  // 'unlimited', 'always', 'page' — always show, never mark as fired
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') {
    return false;
  }
  const key = getStorageKey(stepId, actionId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') {
    return localStorage.getItem(key) === 'fired';
  }
  if (lifecycle === 'session' || lifecycle === 'once_per_session') {
    return sessionStorage.getItem(key) === 'fired';
  }
  if (lifecycle === 'once_per_day') {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return localStorage.getItem(key) === today;
  }
  return false;
}

function markActionFired(stepId: string, actionId: string, lifecycle: string): void {
  if (isDevelopmentRuntime()) {
    return;
  }
  // 'unlimited', 'always', 'page' — no persistence
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') {
    return;
  }
  const key = getStorageKey(stepId, actionId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') {
    localStorage.setItem(key, 'fired');
  } else if (lifecycle === 'session' || lifecycle === 'once_per_session') {
    sessionStorage.setItem(key, 'fired');
  } else if (lifecycle === 'once_per_day') {
    localStorage.setItem(key, new Date().toISOString().slice(0, 10));
  }
}

// ---------------------------------------------------------------------------
// Popup Components
// ---------------------------------------------------------------------------

function UnsupportedFreeGiftActionNotice({ onClose }: { onClose: () => void }) {
  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Unsupported funnel action</h3>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
          {DEPRECATED_FREE_GIFT_POPUP_MESSAGE}
        </p>
        <button type="button" onClick={onClose} style={dismissButtonStyle}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const popupStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '400px',
  width: '90%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
};
const dismissButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  background: 'transparent',
  color: '#666',
  border: '1px solid #ddd',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
};

// ---------------------------------------------------------------------------
// PF-014: Template-based popup
// ---------------------------------------------------------------------------

/**
 * useTemplateBlocks — fetches and caches template page_content blocks.
 *
 * Returns:
 *  - `blocks`  — the resolved StepPageBlock[], empty until fetch resolves
 *  - `loading` — true while the fetch is in-flight
 *  - `failed`  — true when the template cannot be rendered
 */
function useTemplateBlocks(
  templateId: string | undefined,
  fetchTemplate: (id: string) => Promise<TemplateContentResponse>
): {
  blocks: StepPageBlock[];
  loading: boolean;
  failed: boolean;
  settled: boolean;
} {
  const [blocks, setBlocks] = useState<StepPageBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [settledTemplateId, setSettledTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId) {
      setBlocks([]);
      setFailed(false);
      setLoading(false);
      setSettledTemplateId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSettledTemplateId(null);

    fetchTemplate(templateId)
      .then((data) => {
        if (cancelled) return;
        const resolved = normalizeBlocks(data.pageContent);
        if (resolved.length === 0) {
          if (isDevelopmentRuntime()) {
            funnelLogger.warn('ActionExecutor template unavailable: empty template content', {
              templateId,
            });
          }
          setBlocks([]);
          setFailed(true);
        } else {
          if (isDevelopmentRuntime()) {
            funnelLogger.info('ActionExecutor template loaded', {
              templateId,
              blockCount: resolved.length,
            });
          }
          setBlocks(resolved);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isDevelopmentRuntime()) {
          funnelLogger.warn('ActionExecutor template unavailable: template fetch failed', {
            templateId,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        setBlocks([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setSettledTemplateId(templateId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, fetchTemplate]);

  return { blocks, loading, failed, settled: settledTemplateId === templateId };
}

/**
 * TemplatePopup — renders template blocks inside the standard overlay/modal shell.
 * Missing, empty, or unavailable templates fail closed.
 */
function TemplatePopup({
  templateId,
  fetchTemplate,
  onClose,
  onAccept,
  onDecline,
  onRendered,
  onUnavailable,
  fallbackOffer,
}: {
  templateId: string;
  fetchTemplate: (id: string) => Promise<TemplateContentResponse>;
  onClose: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  onRendered: () => void;
  onUnavailable: () => void;
  fallbackOffer?: Partial<FunnelOffer> | null;
}): React.ReactElement {
  const callbacks: BlockCallbacks = { onAccept, onDecline };
  const { blocks, loading, failed, settled } = useTemplateBlocks(templateId, fetchTemplate);
  const notificationRef = useRef<string | null>(null);
  const renderable = settled && !failed && blocks.length > 0;

  useEffect(() => {
    if (!settled) return;
    const status = renderable ? 'rendered' : 'unavailable';
    const notificationKey = `${templateId}:${status}`;
    if (notificationRef.current === notificationKey) return;
    notificationRef.current = notificationKey;
    if (renderable) {
      onRendered();
    } else {
      onUnavailable();
    }
  }, [onRendered, onUnavailable, renderable, settled, templateId]);

  // While loading, render nothing to avoid layout shift.
  if (loading || !settled) return <></>;

  if (failed || blocks.length === 0) return <></>;

  // Build a minimal FunnelOffer for blocks that reference offer data
  const emptyOffer: FunnelOffer = {
    id: '',
    name: '',
    productId: '',
    variantId: '',
    headline: '',
    description: '',
    imageUrl: '',
    ctaText: 'OK',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: null,
    originalPrice: null,
  };

  const resolvedOffer: FunnelOffer = {
    ...emptyOffer,
    ...fallbackOffer,
  };

  const renderedBlocks = renderBlocks(blocks, resolvedOffer, callbacks);

  return (
    <div style={overlayStyle}>
      <div style={{ ...popupStyle, maxWidth: '560px', position: 'relative' }}>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: 'absolute' as const,
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
            color: '#666',
          }}
        >
          ×
        </button>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '12px',
            marginTop: '8px',
          }}
        >
          {renderedBlocks}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionExecutor Component
// ---------------------------------------------------------------------------

export function ActionExecutor({
  actions,
  triggerLifecycle = 'session',
  stepId,
  onAddToCart,
  onOfferAccept,
  onOfferDecline,
  fallbackOffer,
  hopCount = 0,
  commerceEnabled = true,
  intentEnvelope,
  onActionSucceeded,
  onActionSettled,
}: ActionExecutorProps) {
  const { fetchTemplate, eventBus, sessionId, config, resolvePageContext, semanticPorts } =
    useFunnelContext();
  const analytics = useFunnelAnalytics();
  const executedRef = useRef(false);
  const executionAbortRef = useRef<AbortController | null>(null);
  const executionEffectEpochRef = useRef(0);
  // TF-AUD-003: Track last stepId so executedRef resets on downsell chaining.
  const lastStepRef = useRef<string | null>(null);
  const [activePopup, setActivePopup] = useState<
    'leads' | 'offer' | 'unsupported-free-gift' | null
  >(null);
  const [popupConfig, setPopupConfig] = useState<ActionConfig>({});
  const pendingPopupActionRef = useRef<{
    action: RuntimeStepAction;
    type: 'show_leads_popup' | 'display_offer';
  } | null>(null);

  const closePopup = useCallback(() => {
    pendingPopupActionRef.current = null;
    setActivePopup(null);
  }, []);
  const handlePopupRendered = useCallback(() => {
    const pendingAction = pendingPopupActionRef.current;
    if (!pendingAction) return;
    markActionFired(stepId, pendingAction.action.id, triggerLifecycle);
    analytics.trackPopupShown({
      stepId,
      actionType: pendingAction.type,
      actionId: pendingAction.action.id,
    });
    onActionSucceeded?.(pendingAction.action);
    onActionSettled?.(pendingAction.action, true);
    pendingPopupActionRef.current = null;
  }, [
    analytics,
    onActionSettled,
    onActionSucceeded,
    stepId,
    triggerLifecycle,
  ]);
  const handlePopupUnavailable = useCallback(() => {
    const pendingAction = pendingPopupActionRef.current;
    if (pendingAction) {
      onActionSettled?.(pendingAction.action, false);
    }
    closePopup();
  }, [closePopup, onActionSettled]);

  useEffect(() => {
    const effectEpoch = ++executionEffectEpochRef.current;
    const cleanupExecution = () => {
      // React development strict-effects replay tears an effect down and
      // immediately mounts it again. Defer abort by one microtask so the next
      // epoch can retain the same in-flight mutation; a real unmount has no
      // successor epoch and still aborts promptly.
      queueMicrotask(() => {
        if (executionEffectEpochRef.current === effectEpoch) {
          executionAbortRef.current?.abort();
        }
      });
    };

    // TF-AUD-003: Reset on stepId change so downsell/chained step actions fire.
    if (lastStepRef.current !== stepId) {
      executionAbortRef.current?.abort();
      executedRef.current = false;
      lastStepRef.current = stepId;
      setActivePopup(null);
      setPopupConfig({});
      pendingPopupActionRef.current = null;
    }
    if (executedRef.current || actions.length === 0) {
      return cleanupExecution;
    }
    executedRef.current = true;

    // One AbortController per effect invocation — aborted on unmount or re-run.
    const abort = new AbortController();
    executionAbortRef.current = abort;

    const executeActions = async (): Promise<void> => {
      // Execute actions in order and wait for every authoritative boundary.
      let popupShown = false;
      let navigated = false;
      for (const action of actions) {
        if (abort.signal.aborted || popupShown || navigated) break;
        if (hasActionFired(stepId, action.id, triggerLifecycle)) continue;

        // Per-action condition gate (#5): step-level hooks select the step; an
        // action's own hook further gates whether it fires.
        const actionHook = 'hook' in action ? action.hook : undefined;
        if (actionHook && !evaluateStepHook(actionHook, resolvePageContext())) {
          onActionSettled?.(action, false);
          continue;
        }

        if (
          !commerceEnabled &&
          (String(action.type) === 'add_to_cart' || String(action.type) === 'display_offer')
        ) {
          funnelLogger.info('Funnel commerce action held by secure cart proof gate', {
            actionId: action.id,
            actionType: action.type,
            stepId,
          });
          onActionSettled?.(action, false);
          continue;
        }

        if (String(action.type) === DEPRECATED_FREE_GIFT_POPUP_ACTION) {
          markActionFired(stepId, action.id, triggerLifecycle);
          funnelLogger.warn(DEPRECATED_FREE_GIFT_POPUP_MESSAGE, {
            actionId: action.id,
            stepId,
          });
          setPopupConfig(action.config);
          setActivePopup('unsupported-free-gift');
          popupShown = true;
          onActionSettled?.(action, true);
          continue;
        }

        const handler = getActionHandler(String(action.type));
        if (handler) {
          const result = await dispatchAction({
            action,
            config: action.config,
            stepId,
            eventBus,
            sessionId,
            tenantId: config?.tenantId,
            signal: abort.signal,
            logger: funnelLogger,
            featureEnabled: isFunnelUiEventsEnabled,
            hopCount,
            onAddToCart,
            onOfferAccept,
            onOfferDecline,
            semanticPorts,
            intentEnvelope,
          });
          if (abort.signal.aborted) break;
          if (result.status === 'succeeded') {
            markActionFired(stepId, action.id, triggerLifecycle);
            onActionSucceeded?.(action);
          }
          onActionSettled?.(action, result.status === 'succeeded');
          if (result.navigated) {
            navigated = true;
          }
          continue;
        }

        switch (action.type) {
          case 'show_leads_popup': {
            if (!resolveActionTemplateId(action.config)) {
              funnelLogger.warn('ActionExecutor template unavailable: action has no template', {
                actionType: action.type,
                actionId: action.id,
                stepId,
              });
              onActionSettled?.(action, false);
              continue;
            }
            pendingPopupActionRef.current = { action, type: action.type };
            setPopupConfig(action.config);
            setActivePopup('leads');
            popupShown = true;
            break;
          }
          case 'display_offer': {
            if (!resolveActionTemplateId(action.config)) {
              funnelLogger.warn('ActionExecutor template unavailable: action has no template', {
                actionType: action.type,
                actionId: action.id,
                stepId,
              });
              onActionSettled?.(action, false);
              continue;
            }
            pendingPopupActionRef.current = { action, type: action.type };
            setPopupConfig(action.config);
            setActivePopup('offer');
            popupShown = true;
            break;
          }
          default:
            funnelLogger.warn('Unknown funnel action type', {
              actionType: action.type,
              actionId: action.id,
              stepId,
            });
            onActionSettled?.(action, false);
        }
      }
    };

    void executeActions();

    return cleanupExecution;
  }, [
    actions,
    commerceEnabled,
    config?.tenantId,
    eventBus,
    hopCount,
    intentEnvelope,
    onActionSucceeded,
    onActionSettled,
    onAddToCart,
    onOfferAccept,
    onOfferDecline,
    resolvePageContext,
    semanticPorts,
    sessionId,
    stepId,
    triggerLifecycle,
  ]);

  if (activePopup === 'unsupported-free-gift') {
    return <UnsupportedFreeGiftActionNotice onClose={closePopup} />;
  }

  if (activePopup === 'leads') {
    const popupTemplateId = resolveActionTemplateId(popupConfig);
    if (popupTemplateId) {
      return (
        <TemplatePopup
          templateId={popupTemplateId}
          fetchTemplate={fetchTemplate}
          onClose={closePopup}
          onAccept={closePopup}
          onDecline={closePopup}
          onRendered={handlePopupRendered}
          onUnavailable={handlePopupUnavailable}
        />
      );
    }
    return null;
  }

  if (activePopup === 'offer') {
    const popupTemplateId = resolveActionTemplateId(popupConfig);
    const effectiveOfferId = fallbackOffer?.id;
    const handleOfferAccept = async () => {
      try {
        if (effectiveOfferId && onOfferAccept) {
          await onOfferAccept(effectiveOfferId);
        }
      } catch {
        return;
      }
      closePopup();
    };
    const handleOfferDecline = async () => {
      try {
        if (effectiveOfferId && onOfferDecline) {
          await onOfferDecline(effectiveOfferId);
        }
      } catch {
        // Closing a popup is a local UI action and must not depend on the
        // remote decline callback succeeding.
      }
      closePopup();
    };
    if (popupTemplateId) {
      return (
        <TemplatePopup
          templateId={popupTemplateId}
          fetchTemplate={fetchTemplate}
          onClose={handleOfferDecline}
          onAccept={handleOfferAccept}
          onDecline={handleOfferDecline}
          onRendered={handlePopupRendered}
          onUnavailable={handlePopupUnavailable}
          fallbackOffer={fallbackOffer}
        />
      );
    }
    return null;
  }

  return null;
}
